import React from 'react';

type LogoSize = 'sm' | 'md' | 'lg';

const SIZES: Record<LogoSize, { mark: string; text: string }> = {
  sm: { mark: 'w-8 h-8', text: 'text-base' },
  md: { mark: 'w-[38px] h-[38px]', text: 'text-2xl' },
  lg: { mark: 'w-10 h-10', text: 'text-2xl' },
};

interface LogoProps {
  size?: LogoSize;
  /** Render the "Planacle" wordmark beside the mark. Default true. */
  showWordmark?: boolean;
  /**
   * Classes for the wordmark span. Defaults to the theme-aware `text-main`
   * (navy in light, near-white in dark). Override on always-dark surfaces
   * (e.g. the sidebar) where `text-main` would be invisible, or to add
   * responsive visibility (e.g. `hidden lg:inline`).
   */
  wordmarkClassName?: string;
  /** Classes for the outer flex container (margins, alignment). */
  className?: string;
}

/**
 * Planacle brand logo — the transparent multi-colour emblem plus a live
 * "Planacle" wordmark. The emblem has no background, so it sits directly on any
 * surface; the wordmark colour is theme-aware (override via `wordmarkClassName`
 * on always-dark surfaces). Emblem source: `public/logo-emblem.png`.
 */
export const Logo: React.FC<LogoProps> = ({
  size = 'md',
  showWordmark = true,
  wordmarkClassName = 'text-main',
  className = '',
}) => {
  const s = SIZES[size];
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <img
        src='/logo-emblem.png'
        alt={showWordmark ? '' : 'Planacle'}
        aria-hidden={showWordmark || undefined}
        className={`${s.mark} object-contain flex-shrink-0`}
      />
      {showWordmark && (
        <span className={`font-extrabold tracking-tight ${s.text} ${wordmarkClassName}`}>
          Planacle
        </span>
      )}
    </div>
  );
};
